import { useState } from 'react';
import { Modal } from '../Modal';
import { useAppUser } from '../../auth/AppUserContext';
import { reopenTask, type ServerTask } from '../../data/tasks';
import { ActionButton } from '../ActionButton';
import { formatDay, isoDaysAgo } from '../dates';

/** Default new due date for a reopened task: a week from today (owner ruling 2026-10-21). */
export const REOPEN_DUE_DAYS = 7;

/** Company-calendar (Eastern) YYYY-MM-DD `days` from `now`. */
export function dayFromToday(days: number, now: Date = new Date()): string {
  return isoDaysAgo(-days, now);
}

/**
 * The dialog's starting date: a week from today — or the task's own due
 * date when that is later (a task reopened well before its deadline keeps
 * its time; QA review 2026-10-21). Changeable either way.
 */
export function reopenDefaultDue(taskDue: string | null | undefined, now: Date = new Date()): string {
  const week = dayFromToday(REOPEN_DUE_DAYS, now);
  const own = (taskDue ?? '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(own) && own > week ? own : week;
}

/**
 * Why the date box shows that date (round 4, 2026-10-24: the default looked
 * arbitrary when it happened to equal the old due date). Null once changed.
 */
export function reopenDueHint(chosen: string, taskDue: string | null | undefined, now: Date = new Date()): string | null {
  const week = dayFromToday(REOPEN_DUE_DAYS, now);
  const own = (taskDue ?? '').slice(0, 10);
  if (chosen === week) return `${REOPEN_DUE_DAYS} days from today${own === week ? ' (also the task’s own due date)' : ''}.`;
  if (chosen === own && own > week) return 'The task’s own due date, which is more than a week away.';
  return null;
}

/**
 * Reopening asks WHY, the same way completing asks what was done (owner
 * ruling 2026-10-19, after the DM exploration). The reason is written on the
 * reopen event, so it shows in the task's activity feed and Flow 2c quotes
 * it in the one "Task reopened" email to the assignee. Shared by the task
 * page, the Tasks hub and the store/district pages.
 *
 * It also asks for a NEW DUE DATE (owner ruling 2026-10-21, after round 1):
 * a reopened task kept its old, usually passed, date — instantly overdue
 * again, so Flow 3 nagged or escalated at once. Default today + 7, never
 * before today (the server allows one day of timezone slack).
 */
export function ReopenTaskModal({
  task,
  onClose,
  onDone,
}: {
  task: ServerTask;
  onClose: () => void;
  onDone: () => void;
}) {
  const { user } = useAppUser();
  const [reason, setReason] = useState('');
  const today = dayFromToday(0);
  const [dueDate, setDueDate] = useState(() => reopenDefaultDue(task.dueDate));
  // Pre-ticked (owner ruling 2026-10-25, B1): most reopens are for missing proof.
  const [proofRequired, setProofRequired] = useState(true);
  // A typed/picked date before today (the picker's min doesn't stop typing).
  const dueOk = /^\d{4}-\d{2}-\d{2}$/.test(dueDate) && dueDate >= today;
  // Shown inside the dialog — the page's own error line sits behind it.
  const [saveError, setSaveError] = useState<string | null>(null);

  return (
    <Modal onClose={onClose} dirty={reason.trim() !== ''}>
        <h2>Reopen task</h2>
        <p>{task.title}</p>
        <label className="span-2" style={{ display: 'block' }}>
          Why are you reopening it? (required)
          <textarea
            className="q-input"
            rows={3}
            required
            autoFocus
            maxLength={2000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What still needs to be done?"
          />
        </label>
        <label style={{ display: 'block', marginTop: '0.5rem' }}>
          New due date
          <input
            type="date"
            required
            min={today}
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            aria-invalid={!dueOk || undefined}
          />
        </label>
        {dueOk && reopenDueHint(dueDate, task.dueDate) && (
          <p className="muted" style={{ fontSize: '0.85rem', margin: '0.25rem 0 0' }}>
            {reopenDueHint(dueDate, task.dueDate)}
          </p>
        )}
        {!dueOk && (
          <p className="error" style={{ fontSize: '0.85rem', margin: '0.25rem 0 0' }}>
            Pick today or a later day.
          </p>
        )}
        <label className="checkbox checkbox-inline" style={{ marginTop: '0.5rem' }}>
          <input type="checkbox" checked={proofRequired} onChange={(e) => setProofRequired(e.target.checked)} />
          Proof required (photo or PDF to complete)
        </label>
        {/* On by default when reopening (B1 ruling 2026-10-25) — say so, or it reads as a slip (UI review 3, D9). */}
        {!task.proofRequired && proofRequired && (
          <p className="muted" style={{ fontSize: '0.85rem', margin: '0.15rem 0 0' }}>
            Ticked by default — most reopens are for missing proof. Untick it if none is needed.
          </p>
        )}
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          The assignee gets one email with this reason, and it shows in the
          task’s activity{task.dueDate && task.dueDate !== dueDate ? ` — the due date moves from ${formatDay(task.dueDate)} to ${formatDay(dueDate) || '…'}` : ''}.
        </p>
        {saveError && (
          <p className="error" role="alert">
            {saveError}
          </p>
        )}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <ActionButton
            className="btn btn-primary"
            disabled={!reason.trim() || !dueOk || !user}
            busyLabel="Reopening…"
            onAction={async () => {
              if (!user) return;
              setSaveError(null);
              try {
                await reopenTask(task, { upn: user.upn, name: user.displayName }, reason, dueDate, proofRequired);
              } catch (err) {
                setSaveError(err instanceof Error ? err.message : String(err));
                return;
              }
              onDone();
            }}
          >
            Reopen task
          </ActionButton>
        </div>
    </Modal>
  );
}
